import React, { useState } from 'react';
import Button from './Button';
import Icon from '../AppIcon';

const QuickActionToolbar = ({ 
  actions = [], 
  selectedCount = 0, 
  onRefresh, 
  onExport, 
  onFilter,
  className = "" 
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const defaultActions = [
    {
      id: 'refresh',
      label: 'Refresh',
      icon: 'RefreshCw',
      onClick: onRefresh,
      variant: 'ghost'
    },
    {
      id: 'export',
      label: 'Export',
      icon: 'Download',
      onClick: () => setIsExportOpen(!isExportOpen),
      variant: 'ghost'
    },
    {
      id: 'filter',
      label: 'Filter',
      icon: 'Filter',
      onClick: () => setIsFilterOpen(!isFilterOpen),
      variant: 'ghost'
    }
  ];

  const exportOptions = [
    { label: 'Export as CSV', format: 'csv', icon: 'FileText' },
    { label: 'Export as Excel', format: 'xlsx', icon: 'FileSpreadsheet' },
    { label: 'Export as PDF', format: 'pdf', icon: 'FileImage' }
  ];

  const handleExport = (format) => {
    if (onExport) {
      onExport(format);
    }
    setIsExportOpen(false);
  };

  const allActions = actions?.length > 0 ? actions : defaultActions;

  return (
    <div className={`flex items-center justify-between bg-card border border-border rounded-lg p-3 ${className}`}>
      {/* Selection Info */}
      <div className="flex items-center space-x-3">
        {selectedCount > 0 && (
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckSquare" size={16} className="text-primary" />
            <span className="text-foreground font-medium">
              {selectedCount} selected
            </span>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-2">
        {allActions?.map((action) => (
          <div key={action?.id} className="relative">
            <Button
              variant={action?.variant || 'ghost'}
              size="sm"
              onClick={action?.onClick}
              iconName={action?.icon}
              iconSize={16}
              className="text-muted-foreground hover:text-foreground"
            >
              {action?.label}
            </Button>

            {/* Export Dropdown */}
            {action?.id === 'export' && isExportOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="py-2">
                  {exportOptions?.map((option) => (
                    <button
                      key={option?.format}
                      onClick={() => handleExport(option?.format)}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name={option?.icon} size={16} />
                      <span>{option?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Filter Dropdown */}
            {action?.id === 'filter' && isFilterOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-md shadow-elevated z-50">
                <div className="p-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-popover-foreground">
                        Date Range
                      </label>
                      <div className="mt-1 flex space-x-2">
                        <Button variant="outline" size="sm" className="flex-1">
                          Today
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1">
                          This Week
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-popover-foreground">
                        Status
                      </label>
                      <div className="mt-1 space-y-1">
                        <label className="flex items-center space-x-2 text-sm">
                          <input type="checkbox" className="rounded border-border" />
                          <span>Active</span>
                        </label>
                        <label className="flex items-center space-x-2 text-sm">
                          <input type="checkbox" className="rounded border-border" />
                          <span>Pending</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => {
                          if (onFilter) onFilter();
                          setIsFilterOpen(false);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Live Data Indicator */}
        <div className="flex items-center space-x-2 px-3 py-1 bg-muted rounded-md">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse-subtle"></div>
          <span className="text-xs font-medium text-muted-foreground">
            Live
          </span>
        </div>
      </div>
    </div>
  );
};

export default QuickActionToolbar;